// Style tab for text — the whole text-styling surface in one 300px column,
// restructured 2026-07-19 (owner): EVERY section is an accordion in ONE
// exclusive group (at most one open, ALL collapsed on a fresh selection):
//   Styles (here), then Effects / Color / Raised & lowered / Fit /
//   ELA tools / Shape / Highlight / Spacing & letters / More
//   (inside the shared TextCustomizePanel, which joins THIS component's
//   group through openSection/onToggleSection — the state lives here so the
//   whole column obeys one at-most-one-open rule). The former Emphasize
//   accordion is gone (owner, 2026-07-19) — the standard controls route to
//   the highlighted characters instead (engine routeToLiveSelection).
// A header shows a small ACTIVE dot when its section carries live
// non-default state (bevel/3D on, highlight on, …) — the collapsed wall
// never hides what's active. Dots derive from the selection mirrors only.
//
// ① Styles — the COMPACT featured grid (owner, 2026-07-16): small SQUARE
//    swatches, 5 per row, NO labels (name rides the hover title) — each a
//    TRUE preview (stylePreviewRenderer square variant on a short "Aa"
//    sample with the user's font/color — the same composeRecipePatch the
//    engine applies, so a swatch cannot drift from what a click paints),
//    plus the small "Browse all" button (Styles Explorer modal).
// ② The Bevel / 3D depth checkboxes live INSIDE TextCustomizePanel's
//    Effects accordion (owner, 2026-07-19: "remove the add depth section
//    and add the bevel and 3d to the effects accordion with the rest of
//    them"). This tab still owns the Styles Explorer mount, so it passes
//    onOpenExplorer down — the depth block's "More looks…" links deep-link
//    into the explorer at the right rail category ('bevel' | '3d').
// Every user-visible "Extrude" is renamed "3D" (owner, 2026-07-19); the
// engine step id stays 'extrude'.
import React, { useEffect, useState } from 'react';
import { useSelectionProps } from '../../hooks/useSelectionProps';
import { TbAccordion } from '../../primitives/toolbarPrimitives';
import { renderStyleSwatch } from './stylePreviewRenderer';
import styleManifest from './styles/styleManifest.json';
import StylesExplorerModal from './StylesExplorerModal';
import TextCustomizePanel from './TextCustomizePanel';

// Two 5-wide rows max (10 slots). The manifest curates `featured` — never
// pad the grid with invented styles; with ≤8 featured the browse button
// spans the leftover row-2 cells.
const FEATURED_STYLES = styleManifest.styles.filter((s) => s.featured).slice(0, 10);
const GRID_SLOTS = 10;

// Square swatch render size (device px; the ~50px grid cell downscales it,
// so the thumb stays crisp on retina).
const SWATCH_PX = 96;

// A drawer-link request is an IMMEDIATE intent (R2): this tab remounts
// whenever the inspector reopens or the selection changes, and an effect
// keyed on the same {id, n} would REPLAY the link on every remount — with
// 'styles-explorer' that popped the explorer modal on every later text
// selection. `n` is the dispatch timestamp (Date.now() in StudioCoreEditor),
// so freshness IS the consumption rule: honor a request only within this
// window. A once-only ledger can't do the job — StrictMode's dev
// double-mount must be allowed to re-apply, later remounts must not.
const SECTION_REQ_FRESH_MS = 1500;

export default function TextStyleTab({ editor, selectedObjects, sectionRequest }) {
  const { objProps, apply } = useSelectionProps(editor, selectedObjects);
  const [explorerOpen, setExplorerOpen] = useState(false); // pure UI
  const [chipThumbs, setChipThumbs] = useState({}); // styleId → blob URL

  // THE one exclusive accordion group for the whole column (pure UI).
  // null = all closed; clicking an open head closes it. Reset when the
  // selected object changes so every fresh selection greets the teacher with
  // the collapsed wall (the dots say where the action is).
  const [openSection, setOpenSection] = useState(null);
  const selObj = selectedObjects?.[0]; // stable between selection events
  useEffect(() => {
    setOpenSection(null);
  }, [selObj]);

  // Ribbon drawer links (R1): a ribbon "…" names the accordion it unfolds
  // into ({id, n} — the nonce lets the same link fire twice). This is the
  // drawer-is-the-ribbon-unfolded law's crude first face; R3 re-maps the
  // whole column 1:1 to ribbon groups. R2 added ONE pseudo-section:
  // 'styles-explorer' — Row 1's Styles★ goes straight to the explorer modal
  // (this tab owns its mount), landing on the Styles accordion beneath it.
  useEffect(() => {
    if (!sectionRequest?.id) return;
    if (Date.now() - (sectionRequest.n || 0) > SECTION_REQ_FRESH_MS) return; // stale = already served
    if (sectionRequest.id === 'styles-explorer') {
      setOpenSection('styles');
      setExplorerOpen(true);
      return;
    }
    setOpenSection(sectionRequest.id);
  }, [sectionRequest]);
  const toggleSection = (id) => setOpenSection((s) => (s === id ? null : id));
  const acc = (id) => ({ open: openSection === id, onToggle: () => toggleSection(id) });

  const sel = editor?.selectedObjects?.[0]; // sanctioned read-only peek
  const fontFamily = objProps.fontFamily;
  const fontWeight = objProps.fontWeight;
  // The teacher's real text color (objProps.fill's exact derivation, but
  // read from the LIVE object) — kept-fill recipes (Shadow Pop…) preview on
  // it instead of the ground default. Live peek, not the mirror: a color
  // pick in the Color section below rides TextCustomizePanel's OWN hook
  // instance and fires no object:modified, so this instance's mirror goes
  // stale — while every commit bumps historyIndex and re-renders this tree,
  // making the live read fresh exactly when it must be. Part of the
  // renderer's cache key.
  const baseFill = typeof sel?.fill === 'string' && sel.fill !== 'transparent' ? sel.fill : '#000000';

  // Featured swatches render eagerly (≈2 ms each, LRU-cached by the renderer)
  // on the SHORT "Aa" sample — square by design, so they re-render only when
  // the font/color change, never per keystroke. Ground guess: 'light'
  // (sidebar swatches sit on a light chip face).
  useEffect(() => {
    if (selectedObjects.length !== 1) return undefined;
    let alive = true;
    FEATURED_STYLES.forEach((s) => {
      renderStyleSwatch({
        recipe: s.recipe,
        fontFamily,
        fontWeight,
        ground: 'light',
        size: SWATCH_PX,
        cacheKey: `swatch|${s.id}`,
        baseFill, // keyFor appends it — no cacheKey change needed
      }).then((url) => {
        if (alive) setChipThumbs((m) => ({ ...m, [s.id]: url }));
      }).catch(() => { /* swatch keeps its Aa placeholder */ });
    });
    return () => { alive = false; };
  }, [fontFamily, fontWeight, baseFill, selectedObjects.length]);

  if (selectedObjects.length > 1) {
    return (
      <div className="cce-inspector-content">
        <div className="cce-inspector-empty">Select one text item to style it.</div>
      </div>
    );
  }

  return (
    <div className="cce-inspector-content cce-accordion-wall">
      {/* ① Styles — compact featured grid (5-wide squares + Browse all). */}
      <TbAccordion title="Styles" {...acc('styles')}>
        <div className="cce-styles-grid cce-styles-grid-featured">
          {FEATURED_STYLES.map((s) => (
            <button
              key={s.id}
              type="button"
              className="cce-style-chip"
              title={s.label}
              onClick={() => apply(() => editor?.applyTextStyle({ id: s.id, label: s.label, recipe: s.recipe }), 'now')}
            >
              {chipThumbs[s.id]
                ? <img className="cce-style-chip-img" src={chipThumbs[s.id]} alt="" draggable={false} />
                : <span className="cce-style-chip-text">Aa</span>}
            </button>
          ))}
          {GRID_SLOTS - FEATURED_STYLES.length >= 2 && (
            <button
              type="button"
              className="cce-inspector-mini-btn cce-styles-browse cce-styles-browse-cell"
              style={{ gridColumn: `span ${GRID_SLOTS - FEATURED_STYLES.length}` }}
              onClick={() => setExplorerOpen(true)}
            >
              Browse all
            </button>
          )}
        </div>
        {GRID_SLOTS - FEATURED_STYLES.length < 2 && (
          <button
            type="button"
            className="cce-inspector-mini-btn cce-styles-browse cce-styles-browse-line"
            onClick={() => setExplorerOpen(true)}
          >
            Browse all
          </button>
        )}
      </TbAccordion>

      {/* ② the shared customize surface (own useSelectionProps instance),
          joined into THIS component's exclusive group. onOpenExplorer wires
          the depth block's "More looks…" links (inside its Effects
          accordion) to the Styles Explorer mounted below. */}
      <TextCustomizePanel
        editor={editor}
        selectedObjects={selectedObjects}
        openSection={openSection}
        onToggleSection={toggleSection}
        onOpenExplorer={(cat) => setExplorerOpen(cat)}
      />

      {explorerOpen && (
        <StylesExplorerModal
          editor={editor}
          selectedObjects={selectedObjects}
          initialCategory={typeof explorerOpen === 'string' ? explorerOpen : undefined}
          onClose={() => setExplorerOpen(false)}
        />
      )}
    </div>
  );
}
